import React, { useState } from 'react';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

export default function LibrarySidebarPage() {
  const [activeCategory, setActiveCategory] = useState('All Modules');

  const stripedBg = {
    backgroundImage: 'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)'
  };

  const categories = [
    { name: 'All Modules', icon: '📚', count: 6 },
    { name: 'Content Creation', icon: '🎬', count: 2 },
    { name: 'Marketing Strategy', icon: '📈', count: 2 },
    { name: 'Personal Branding', icon: '✨', count: 1 },
    { name: 'SEO & Analysis', icon: '🔍', count: 1 },
  ];

  // Updated Courses with Prices and High-Quality Dummy Images
  const courses = [
    { 
      id: 1, 
      title: 'Power Social Media Content Creation', 
      category: 'Content Creation', 
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=600&auto=format&fit=crop'
    },
    { 
      id: 2, 
      title: 'ChatGPT for Personal Branding', 
      category: 'Personal Branding', 
      image: 'https://images.unsplash.com/photo-1493612276216-ee3925520721?q=80&w=600&auto=format&fit=crop'
    },
    { 
      id: 3, 
      title: 'Using AI & ChatGPT for Viral Content', 
      category: 'Content Creation', 
      image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?q=80&w=600&auto=format&fit=crop'
    },
    { 
      id: 4, 
      title: 'Mastering AI in Marketing & PR', 
      category: 'Marketing Strategy', 
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=600&auto=format&fit=crop'
    },
    { 
      id: 5, 
      title: 'Winning Marketing Plan Video Course', 
      category: 'Marketing Strategy', 
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=600&auto=format&fit=crop'
    },
    { 
      id: 6, 
      title: 'AI Analysis & SEO Strategies', 
      category: 'SEO & Analysis', 
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=600&auto=format&fit=crop'
    },
  ];

  // Filter logic
  const filteredCourses = activeCategory === 'All Modules' 
    ? courses 
    : courses.filter(course => course.category === activeCategory);

  return (
    // Increased mobile top padding from py-4 to pt-2 pb-4
    <div style={stripedBg} className="min-h-screen w-full pt-2 pb-4 px-2 sm:py-8 sm:px-4 lg:px-12 flex justify-center font-sans text-[var(--text-900)] overflow-hidden">
      
      <div
        className="w-full  bg-[var(--bg-box)] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border border-[var(--border-200)] flex flex-col relative"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />  

        {/* --- MAIN LAYOUT (SIDEBAR + CONTENT) --- */}
        {/* Increased top margin on mobile: mt-8 sm:mt-10 lg:mt-6 */}
        <div className="flex flex-col lg:flex-row flex-grow w-full mt-8 sm:mt-10 lg:mt-6">
          
          {/* --- SIDEBAR --- */}
          <aside className="w-full lg:w-72 lg:flex-shrink-0 border-b lg:border-b-0 lg:border-r border-[var(--border-200)] bg-[var(--bg-white)] p-5 sm:p-6 lg:p-8 flex flex-col z-10">
            
            <div className="lg:sticky lg:top-32">
              <h2 className="hidden lg:block text-xs font-bold tracking-widest text-[var(--text-400)] uppercase mb-6 pl-2">
                Library Navigation
              </h2>

              {/* Mobile Horizontal Scroll / Desktop Vertical Stack */}
              <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto snap-x lg:snap-none lg:overflow-x-visible pb-2 lg:pb-0 hide-scrollbar w-full">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => setActiveCategory(cat.name)}
                    className={`flex items-center justify-between px-5 py-3 rounded-xl transition-all flex-shrink-0 snap-start whitespace-nowrap lg:whitespace-normal ${
                      activeCategory === cat.name
                        ? 'bg-[#2e3543] text-white shadow-md'
                        : 'bg-transparent text-[var(--text-600)] hover:bg-[var(--surface-50)] hover:text-[#2e3543]'
                    }`}
                  >
                    <div className="flex items-center gap-2 lg:gap-3 font-semibold text-sm">
                      <span className="text-base lg:text-lg">{cat.icon}</span>
                      {cat.name}
                    </div>
                    {/* Count Badge (Hidden on Mobile to save space) */}
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md hidden lg:block ${
                      activeCategory === cat.name 
                        ? 'bg-white/20 text-white' 
                        : 'bg-[var(--surface-100)] text-[var(--text-500)]'
                    }`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* --- MAIN CONTENT AREA --- */}
          <main className="flex-1 bg-[var(--surface-50)] p-5 sm:p-8 lg:p-12 w-full">
            
            {/* Content Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 sm:mb-10 gap-4 sm:gap-6">
              <div className="w-full">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#2e3543] mb-3">
                  {activeCategory}
                </h1>
                <p className="text-[var(--text-500)] font-medium text-sm sm:text-base max-w-3xl">
                  {activeCategory === 'All Modules' 
                    ? 'Explore our entire collection of courses covering content creation, SEO, personal branding, and marketing strategies.'
                    : `Dive deep into our specialized ${activeCategory.toLowerCase()} frameworks and courses.`}
                </p>
              </div>
            </div>

            {/* Course Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 pb-10 sm:pb-20">
              {filteredCourses.map(course => (
                <div 
                  key={course.id} 
                  className="bg-white border border-[var(--border-200)] rounded-2xl md:rounded-[1.5rem] overflow-hidden flex flex-col group hover:-translate-y-1 hover:border-[#2e3543] transition-all duration-300 cursor-pointer shadow-sm hover:shadow-xl hover:shadow-slate-200"
                >
                  {/* Thumbnail */}
                  <div className="w-full aspect-video bg-[var(--surface-100)] relative overflow-hidden border-b border-[var(--border-100)]">
                    <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  
                  {/* Card Body */}
                  <div className="p-5 sm:p-6 flex flex-col flex-grow">
                    
                    
                    {/* Line Clamp to keep titles uniform */}
                    <h3 className="text-lg sm:text-xl font-black text-[#2e3543] mb-4 leading-snug group-hover:text-slate-600 transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    
               
                  </div>
                </div>
              ))}
            </div>

          </main>
        </div>
        <Footer />
      </div>
      
      {/* Hide scrollbar for mobile category nav */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}